import React, { useState, useEffect } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import {
    Droplets, Sun, Thermometer, ShoppingCart, Calendar, Gift,
    ChevronRight, Settings, FlaskConical, Bell, AlertTriangle,
    CheckCircle, Clock, MapPin, Loader2, TrendingUp, Package,
    Waves
} from 'lucide-react';
import { Button } from '../components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Badge } from '../components/ui/badge';
import { Progress } from '../components/ui/progress';
import { apiRequest, formatPrice } from '../lib/utils';
import { useAuth } from '../context/AuthContext';
import { toast } from 'sonner';
import AnalysisHistoryChart from '../components/AnalysisHistoryChart';

export default function MyPoolPage() {
    const { t } = useTranslation();
    const { user } = useAuth();
    const navigate = useNavigate();
    const [dashboard, setDashboard] = useState(null);
    const [quickActions, setQuickActions] = useState([]);
    const [weather, setWeather] = useState(null);
    const [loading, setLoading] = useState(true);

    useEffect(() => {
        if (user) {
            loadDashboard();
        }
    }, [user]);

    const loadDashboard = async () => {
        setLoading(true);
        try {
            const [dashboardData, actionsData] = await Promise.all([
                apiRequest('/dashboard/my-pool'),
                apiRequest('/dashboard/quick-actions'),
            ]);

            setDashboard(dashboardData);
            setQuickActions(actionsData.actions || []);

            // Load weather if location is set
            if (dashboardData.weather_location) {
                try {
                    const weatherData = await apiRequest('/weather', {
                        method: 'POST',
                        body: JSON.stringify(dashboardData.weather_location)
                    });
                    setWeather(weatherData);
                } catch (e) {
                    console.error('Weather error:', e);
                }
            }
        } catch (error) {
            console.error('Dashboard error:', error);
            toast.error('Erreur lors du chargement du tableau de bord');
        } finally {
            setLoading(false);
        }
    };

    if (!user) {
        return (
            <div className="min-h-screen bg-gradient-to-b from-background to-muted/30 py-12">
                <div className="max-w-4xl mx-auto px-4 text-center">
                    <Droplets className="h-16 w-16 mx-auto text-primary mb-4" />
                    <h1 className="text-3xl font-bold mb-4">Ma Piscine</h1>
                    <p className="text-muted-foreground mb-6">
                        Connectez-vous pour accéder à votre tableau de bord personnalisé
                    </p>
                    <Button onClick={() => navigate('/auth')}>
                        Se connecter
                    </Button>
                </div>
            </div>
        );
    }

    if (loading) {
        return (
            <div className="min-h-screen bg-gradient-to-b from-background to-muted/30 flex items-center justify-center">
                <Loader2 className="h-8 w-8 animate-spin text-primary" />
            </div>
        );
    }

    const getHealthColor = (health) => {
        switch (health) {
            case 'good': return 'text-green-500';
            case 'warning': return 'text-amber-500';
            case 'critical': return 'text-red-500';
            default: return 'text-muted-foreground';
        }
    };

    const getHealthLabel = (health) => {
        switch (health) {
            case 'good': return 'Excellent';
            case 'warning': return 'Attention requise';
            case 'critical': return 'Action urgente';
            default: return 'Non analysée';
        }
    };

    const getAnalysisStatusColor = (status) => {
        switch (status) {
            case 'recent': return 'bg-green-100 text-green-700';
            case 'due_soon': return 'bg-amber-100 text-amber-700';
            case 'overdue': return 'bg-red-100 text-red-700';
            default: return 'bg-muted text-muted-foreground';
        }
    };

    return (
        <div className="min-h-screen bg-gradient-to-b from-background to-muted/30 py-8" data-testid="my-pool-page">
            <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
                {/* Header */}
                <div className="flex items-center justify-between mb-8">
                    <div>
                        <h1 className="text-3xl font-bold flex items-center gap-3">
                            <Droplets className="h-8 w-8 text-primary" />
                            Ma Piscine
                        </h1>
                        <p className="text-muted-foreground mt-1">
                            Bienvenue, {user.name}
                        </p>
                    </div>
                    {dashboard?.stats?.unread_notifications > 0 && (
                        <Link to="/notifications">
                            <Button variant="outline" className="relative">
                                <Bell className="h-4 w-4" />
                                <Badge className="absolute -top-2 -right-2 h-5 w-5 flex items-center justify-center p-0 text-xs">
                                    {dashboard.stats.unread_notifications}
                                </Badge>
                            </Button>
                        </Link>
                    )}
                </div>

                {/* Quick Actions */}
                {quickActions.length > 0 && (
                    <div className="mb-8">
                        <h2 className="text-lg font-semibold mb-4">Actions suggérées</h2>
                        <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-4">
                            {quickActions.map((action) => (
                                <Link key={action.id} to={action.link}>
                                    <Card className="hover:shadow-md transition-shadow cursor-pointer border-l-4 border-l-primary">
                                        <CardContent className="py-4">
                                            <h3 className="font-semibold">{action.title}</h3>
                                            <p className="text-sm text-muted-foreground mt-1">
                                                {action.description}
                                            </p>
                                        </CardContent>
                                    </Card>
                                </Link>
                            ))}
                        </div>
                    </div>
                )}

                {/* Main Grid */}
                <div className="grid lg:grid-cols-3 gap-6">
                    {/* Left Column */}
                    <div className="lg:col-span-2 space-y-6">
                        {/* Pool Health Overview */}
                        <Card>
                            <CardHeader className="flex flex-row items-center justify-between">
                                <CardTitle className="flex items-center gap-2">
                                    <FlaskConical className="h-5 w-5" />
                                    État de l'eau
                                </CardTitle>
                                <Link to="/analysis">
                                    <Button size="sm">
                                        Nouvelle analyse
                                        <ChevronRight className="h-4 w-4 ml-1" />
                                    </Button>
                                </Link>
                            </CardHeader>
                            <CardContent>
                                {dashboard?.latest_analysis?.data ? (
                                    <div className="space-y-4">
                                        <div className="flex items-center justify-between">
                                            <div>
                                                <span className={`text-2xl font-bold ${getHealthColor(dashboard.latest_analysis.pool_health)}`}>
                                                    {getHealthLabel(dashboard.latest_analysis.pool_health)}
                                                </span>
                                                <p className="text-sm text-muted-foreground">
                                                    Dernière analyse il y a {dashboard.latest_analysis.days_ago} jour(s)
                                                </p>
                                            </div>
                                            <Badge className={getAnalysisStatusColor(dashboard.latest_analysis.status)}>
                                                {dashboard.latest_analysis.status === 'recent' && 'À jour'}
                                                {dashboard.latest_analysis.status === 'due_soon' && 'Bientôt due'}
                                                {dashboard.latest_analysis.status === 'overdue' && 'En retard'}
                                            </Badge>
                                        </div>

                                        {/* Quick params */}
                                        <div className="grid grid-cols-4 gap-4 pt-4 border-t">
                                            {['ph', 'chlorine', 'calcium', 'stabilizer'].map((param) => {
                                                const result = dashboard.latest_analysis.data.results?.[param];
                                                if (!result) return null;
                                                return (
                                                    <div key={param} className="text-center">
                                                        <p className="text-xs text-muted-foreground uppercase">
                                                            {param === 'chlorine' ? 'Chlore' : param === 'calcium' ? 'Calcaire' : param === 'stabilizer' ? 'Stabilisant' : 'pH'}
                                                        </p>
                                                        <p className={`text-lg font-bold ${
                                                            result.status === 'ideal' ? 'text-green-500' :
                                                            result.status?.includes('critical') ? 'text-red-500' : 'text-amber-500'
                                                        }`}>
                                                            {result.value}
                                                        </p>
                                                    </div>
                                                );
                                            })}
                                        </div>
                                    </div>
                                ) : (
                                    <div className="text-center py-8">
                                        <FlaskConical className="h-12 w-12 mx-auto text-muted-foreground mb-4" />
                                        <p className="text-muted-foreground">
                                            Aucune analyse effectuée
                                        </p>
                                        <Link to="/analysis">
                                            <Button className="mt-4">
                                                Faire ma première analyse
                                            </Button>
                                        </Link>
                                    </div>
                                )}
                            </CardContent>
                        </Card>

                        {/* Analysis History Chart (unified component) */}
                        <AnalysisHistoryChart compact />

                        {/* Recommended Products */}
                        {dashboard?.recommended_products?.length > 0 && (
                            <Card>
                                <CardHeader className="flex flex-row items-center justify-between">
                                    <CardTitle className="flex items-center gap-2">
                                        <Package className="h-5 w-5" />
                                        Produits recommandés
                                    </CardTitle>
                                    <Link to="/shop">
                                        <Button variant="ghost" size="sm">
                                            Voir tout
                                            <ChevronRight className="h-4 w-4 ml-1" />
                                        </Button>
                                    </Link>
                                </CardHeader>
                                <CardContent>
                                    <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                                        {dashboard.recommended_products.map((product) => (
                                            <Link 
                                                key={product.product_id} 
                                                to={`/shop/${product.product_id}`}
                                                className="group"
                                            >
                                                <div className="aspect-square rounded-lg overflow-hidden bg-muted mb-2">
                                                    <img 
                                                        src={product.image_url} 
                                                        alt={product.name}
                                                        className="w-full h-full object-cover group-hover:scale-105 transition-transform"
                                                    />
                                                </div>
                                                <p className="text-sm font-medium line-clamp-2 group-hover:text-primary transition-colors">
                                                    {product.name}
                                                </p>
                                                <p className="text-sm font-bold text-primary">
                                                    {formatPrice(product.price)}
                                                </p>
                                            </Link>
                                        ))}
                                    </div>
                                </CardContent>
                            </Card>
                        )}
                    </div>

                    {/* Right Column */}
                    <div className="space-y-6">
                        {/* Weather Card */}
                        {weather && (
                            <Card>
                                <CardHeader className="pb-2">
                                    <CardTitle className="flex items-center gap-2 text-base">
                                        <Sun className="h-5 w-5 text-amber-500" />
                                        Météo piscine
                                    </CardTitle>
                                </CardHeader>
                                <CardContent>
                                    <div className="flex items-center justify-between">
                                        <div>
                                            <p className="text-3xl font-bold">
                                                {Math.round(weather.current?.temperature)}°C
                                            </p>
                                            <p className="text-xs text-muted-foreground">
                                                Ressenti {Math.round(weather.current?.feels_like || weather.current?.temperature)}°C
                                            </p>
                                            <p className="text-sm text-muted-foreground flex items-center gap-1 mt-1">
                                                <MapPin className="h-3 w-3" />
                                                {dashboard?.weather_location?.city_name || 'Flayosc'}
                                            </p>
                                        </div>
                                        <div className="text-right">
                                            {weather.pool?.water_temp_estimate ? (
                                                <div className="flex items-center gap-1 justify-end">
                                                    <Waves className="h-4 w-4 text-sky-500" />
                                                    <span className="font-semibold text-sky-500">{weather.pool.water_temp_estimate}°C</span>
                                                </div>
                                            ) : null}
                                            <p className="text-xs text-muted-foreground">Eau estimée</p>
                                            <p className="text-xs text-muted-foreground mt-1">UV: {weather.current?.uv_index}</p>
                                        </div>
                                    </div>

                                    {/* Swim index */}
                                    {weather.pool?.swim_index && (
                                        <div className="mt-3 p-2 rounded-lg bg-muted/50">
                                            <div className="flex items-center justify-between text-xs mb-1">
                                                <span className="text-muted-foreground">Indice de baignade</span>
                                                <span className={`font-semibold text-${weather.pool.swim_index.color}-500`}>
                                                    {weather.pool.swim_index.label} · {weather.pool.swim_index.score}/100
                                                </span>
                                            </div>
                                            <Progress value={weather.pool.swim_index.score} className="h-1.5" />
                                        </div>
                                    )}

                                    {weather.alerts?.length > 0 && (
                                        <div className="mt-3 p-2 bg-amber-500/10 rounded-lg">
                                            <p className="text-sm text-amber-700 dark:text-amber-400 flex items-center gap-1">
                                                <AlertTriangle className="h-4 w-4" />
                                                {weather.alerts[0].title}
                                            </p>
                                        </div>
                                    )}
                                    <Link to="/weather">
                                        <Button variant="ghost" size="sm" className="w-full mt-3">
                                            Voir les prévisions
                                            <ChevronRight className="h-4 w-4 ml-1" />
                                        </Button>
                                    </Link>
                                </CardContent>
                            </Card>
                        )}

                        {/* Pool Profile */}
                        <Card>
                            <CardHeader className="pb-2">
                                <CardTitle className="flex items-center gap-2 text-base">
                                    <Settings className="h-5 w-5" />
                                    Ma piscine
                                </CardTitle>
                            </CardHeader>
                            <CardContent>
                                {dashboard?.pool_configured ? (
                                    <div className="space-y-2 text-sm">
                                        <div className="flex justify-between">
                                            <span className="text-muted-foreground">Type</span>
                                            <span className="font-medium">{dashboard.pool_profile?.pool_type || '-'}</span>
                                        </div>
                                        <div className="flex justify-between">
                                            <span className="text-muted-foreground">Volume</span>
                                            <span className="font-medium">{dashboard.pool_profile?.volume || '-'} m³</span>
                                        </div>
                                        <div className="flex justify-between">
                                            <span className="text-muted-foreground">Traitement</span>
                                            <span className="font-medium">{dashboard.pool_profile?.treatment_system || '-'}</span>
                                        </div>
                                        <Link to="/pool-config">
                                            <Button variant="ghost" size="sm" className="w-full mt-2">
                                                Modifier
                                            </Button>
                                        </Link>
                                    </div>
                                ) : (
                                    <div className="text-center py-4">
                                        <p className="text-sm text-muted-foreground mb-3">
                                            Configurez votre piscine pour des conseils personnalisés
                                        </p>
                                        <Link to="/pool-config">
                                            <Button size="sm">
                                                Configurer
                                            </Button>
                                        </Link>
                                    </div>
                                )}
                            </CardContent>
                        </Card>

                        {/* Loyalty Points */}
                        <Card>
                            <CardHeader className="pb-2">
                                <CardTitle className="flex items-center gap-2 text-base">
                                    <Gift className="h-5 w-5 text-amber-500" />
                                    Points fidélité
                                </CardTitle>
                            </CardHeader>
                            <CardContent>
                                <div className="text-center">
                                    <p className="text-3xl font-bold text-primary">
                                        {dashboard?.loyalty?.points || 0}
                                    </p>
                                    <p className="text-sm text-muted-foreground">
                                        = {dashboard?.loyalty?.value_euros || 0}€ de réduction
                                    </p>
                                </div>
                                <Link to="/loyalty">
                                    <Button variant="ghost" size="sm" className="w-full mt-3">
                                        Voir mon programme
                                        <ChevronRight className="h-4 w-4 ml-1" />
                                    </Button>
                                </Link>
                            </CardContent>
                        </Card>

                        {/* Recent Orders */}
                        {dashboard?.recent_orders?.length > 0 && (
                            <Card>
                                <CardHeader className="pb-2">
                                    <CardTitle className="flex items-center gap-2 text-base">
                                        <ShoppingCart className="h-5 w-5" />
                                        Commandes récentes
                                    </CardTitle>
                                </CardHeader>
                                <CardContent>
                                    <div className="space-y-3">
                                        {dashboard.recent_orders.map((order) => (
                                            <Link 
                                                key={order.order_id}
                                                to={`/orders/${order.order_id}`}
                                                className="flex items-center justify-between py-2 border-b last:border-0 hover:bg-muted/50 -mx-2 px-2 rounded"
                                            >
                                                <div>
                                                    <p className="text-sm font-medium">{order.order_id}</p>
                                                    <p className="text-xs text-muted-foreground">
                                                        {formatPrice(order.total)}
                                                    </p>
                                                </div>
                                                <Badge variant="secondary" className="text-xs">
                                                    {order.status}
                                                </Badge>
                                            </Link>
                                        ))}
                                    </div>
                                    <Link to="/orders">
                                        <Button variant="ghost" size="sm" className="w-full mt-2">
                                            Toutes mes commandes
                                        </Button>
                                    </Link>
                                </CardContent>
                            </Card>
                        )}

                        {/* Appointments */}
                        {dashboard?.pending_appointments?.length > 0 && (
                            <Card>
                                <CardHeader className="pb-2">
                                    <CardTitle className="flex items-center gap-2 text-base">
                                        <Calendar className="h-5 w-5" />
                                        Rendez-vous
                                    </CardTitle>
                                </CardHeader>
                                <CardContent>
                                    <div className="space-y-3">
                                        {dashboard.pending_appointments.map((apt) => (
                                            <div key={apt.appointment_id} className="py-2 border-b last:border-0">
                                                <p className="text-sm font-medium">{apt.service_type}</p>
                                                <p className="text-xs text-muted-foreground">
                                                    {apt.preferred_date} à {apt.preferred_time}
                                                </p>
                                                <Badge variant="secondary" className="text-xs mt-1">
                                                    {apt.status}
                                                </Badge>
                                            </div>
                                        ))}
                                    </div>
                                </CardContent>
                            </Card>
                        )}
                    </div>
                </div>
            </div>
        </div>
    );
}
